import React, { useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { Markdown } from "tiptap-markdown";
import {
  Bold,
  Italic,
  UnderlineIcon,
  Code,
  List,
  ListOrdered,
  LinkIcon,
  X,
  Check,
} from "./Icons";

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const MenuBar = ({ editor, disabled }: { editor: any; disabled?: boolean }) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [hasSelection, setHasSelection] = useState(false);

  if (!editor || disabled) {
    return null;
  }

  const openLinkInput = () => {
    const previousUrl = editor.getAttributes("link").href || "";
    const { from, to, empty } = editor.state.selection;

    setLinkUrl(previousUrl);
    setHasSelection(!empty);

    if (empty) {
      setLinkText("");
    } else {
      setLinkText(editor.state.doc.textBetween(from, to, " "));
    }

    setShowLinkInput(true);
  };

  const applyLink = () => {
    if (linkUrl === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      if (!hasSelection && linkText) {
        editor
          .chain()
          .focus()
          .insertContent(`<a href="${linkUrl}">${linkText}</a>`)
          .run();
      } else {
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: linkUrl })
          .run();
      }
    }
    setShowLinkInput(false);
    setLinkUrl("");
    setLinkText("");
  };

  const cancelLink = () => {
    setShowLinkInput(false);
    setLinkUrl("");
    setLinkText("");
  };

  return (
    <div className="flex flex-col border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 rounded-t-lg">
      <div className="flex flex-wrap gap-1 p-1">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`p-1.5 rounded transition ${editor.isActive("bold") ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400" : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"}`}
          title="Bold"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`p-1.5 rounded transition ${editor.isActive("italic") ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400" : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"}`}
          title="Italic"
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-1.5 rounded transition ${editor.isActive("underline") ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400" : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"}`}
          title="Underline"
        >
          <UnderlineIcon size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`p-1.5 rounded transition ${editor.isActive("codeBlock") ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400" : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"}`}
          title="Code Block"
        >
          <Code size={16} />
        </button>
        <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1 self-center" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-1.5 rounded transition ${editor.isActive("bulletList") ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400" : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"}`}
          title="Bullet List"
        >
          <List size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-1.5 rounded transition ${editor.isActive("orderedList") ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400" : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"}`}
          title="Ordered List"
        >
          <ListOrdered size={16} />
        </button>
        <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1 self-center" />
        <button
          type="button"
          onClick={openLinkInput}
          className={`p-1.5 rounded transition ${editor.isActive("link") ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400" : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"}`}
          title="Add Link"
        >
          <LinkIcon size={16} />
        </button>
      </div>

      {showLinkInput && (
        <div className="flex flex-col gap-2 p-2 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 animate-in slide-in-from-top-1">
          <div className="flex flex-wrap items-center gap-2">
            {!hasSelection && (
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Link text"
                className="flex-1 min-w-[120px] text-xs px-2 py-1.5 border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-indigo-500"
              />
            )}
            <input
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="Enter URL (https://...)"
              autoFocus
              className="flex-[2] min-w-[180px] text-xs px-2 py-1.5 border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-indigo-500"
              onKeyDown={(e) => {
                if (e.key === "Enter") applyLink();
                if (e.key === "Escape") cancelLink();
              }}
            />
            <div className="flex items-center gap-1 ml-auto">
              <button
                onClick={applyLink}
                className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded transition"
                title="Apply"
              >
                <Check size={14} />
              </button>
              <button
                onClick={cancelLink}
                className="p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition"
                title="Cancel"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = "Start typing...",
  disabled = false,
  className = "",
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Underline,
      Markdown,
      Placeholder.configure({
        placeholder,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class:
            "text-indigo-600 dark:text-indigo-400 underline underline-offset-4 cursor-pointer",
        },
      }),
    ],
    content,
    immediatelyRender: false,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.storage.markdown.getMarkdown());
    },
  });

  // Sync content if it changes externally
  useEffect(() => {
    if (editor && content !== editor.storage.markdown.getMarkdown()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  return (
    <div
      className={`flex flex-col border border-slate-300 dark:border-slate-600 rounded-lg overflow-hidden bg-white dark:bg-slate-900 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all ${disabled ? "opacity-80 shadow-none" : "shadow-sm"} ${className}`}
    >
      <MenuBar editor={editor} disabled={disabled} />
      <div
        className="flex-grow p-3 min-h-[100px] cursor-text"
        onClick={() => editor?.commands.focus()}
      >
      <div
        className="flex-grow p-3 min-h-[100px] cursor-text"
        onClick={() => editor?.commands.focus()}
      >
        <EditorContent
          editor={editor}
          className="prose prose-sm dark:prose-invert max-w-none outline-none editor-content"
        />
      </div>
      </div>
    </div>
  );
};

interface RichTextViewerProps {
  content: string;
  className?: string;
}

export const RichTextViewer: React.FC<RichTextViewerProps> = ({
  content,
  className = "",
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Markdown,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class:
            "text-indigo-600 dark:text-indigo-400 underline underline-offset-4 cursor-pointer",
        },
      }),
    ],
    content,
    immediatelyRender: false,
    editable: false,
  });

  // Sync content if it changes externally
  useEffect(() => {
    if (editor && content !== editor.storage.markdown.getMarkdown()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  return (
    <div className={`rich-text-viewer ${className}`}>
      <EditorContent
        editor={editor}
        className="prose prose-sm dark:prose-invert max-w-none outline-none"
      />
    </div>
  );
};
